import i18next from 'i18next';
import React from 'react';

import { getChannelSelectionItems } from '@commons/utils/translations';
import { InpulseLabel } from '@commons/DeepsightComponents';
import { labelInAdvancedFilterStyle } from '@commons/constants/labelStyle';

import { getExternalOrderStatusFilterChoices } from '../constants';

/**
 * Filter entries fed to the DeepsightFiltersButton of the external orders list. They are declared
 * apart from the ListView columns because both overload `propertyKey` for different purposes:
 * ListView sorts on it (`externalClientName`, `channelName`), while the filters turn it into the
 * request body key expected by POST /v2/b2b/external-orders/paginated (`externalClientId`,
 * `channelId`). Each `string` choice therefore carries a property named after the filter key,
 * which `getQueryParamOperation` maps to an `{ inArray: [...] }` condition.
 * The store filter is not declared here: the filters modal owns its own store dropdown, whose
 * selection travels as the `storeIds` perimeter of the request.
 *
 * @param {Object} params
 * @param {Array<Object>} params.externalClients - The clients of the perimeter ({ id, name })
 * @param {Array<Object>} params.channels - The client channels ({ id, name, translationKey })
 * @param {boolean} params.hasMultipleChannels - Whether the platform handles several channels
 *
 * @returns {Array<Object>} The columnsFilterList entries
 */
export const getExternalOrderFilterList = ({ externalClients, channels, hasMultipleChannels }) => {
  const filterList = [];

  if (externalClients.length) {
    filterList.push({
      id: 'externalClientId',
      propertyKey: 'externalClientId',
      name: i18next.t('EXTERNAL_ORDERS.LIST.COLUMN_CLIENT'),
      filterType: 'string',
      list: externalClients.map(({ id, name }) => ({ id, name, externalClientId: id })),
    });
  }

  if (hasMultipleChannels && channels.length) {
    filterList.push({
      id: 'channelId',
      propertyKey: 'channelId',
      name: i18next.t('EXTERNAL_ORDERS.LIST.COLUMN_CHANNEL'),
      filterType: 'string',
      list: getChannelSelectionItems(channels).map(({ id, name }) => ({
        id,
        name,
        channelId: id,
      })),
    });
  }

  filterList.push(
    {
      id: 'deliveryDate',
      propertyKey: 'deliveryDate',
      name: i18next.t('EXTERNAL_ORDERS.LIST.COLUMN_DELIVERY_DATE'),
      filterType: 'date',
    },
    {
      id: 'deliveryTime',
      propertyKey: 'deliveryTime',
      name: i18next.t('EXTERNAL_ORDERS.LIST.COLUMN_DELIVERY_TIME'),
      filterType: 'time',
    },
    {
      id: 'totalWithTaxes',
      propertyKey: 'totalWithTaxes',
      name: i18next.t('EXTERNAL_ORDERS.LIST.COLUMN_TOTAL_WITH_TAXES'),
      filterType: 'numeric',
    },
    {
      id: 'status',
      propertyKey: 'status',
      name: i18next.t('EXTERNAL_ORDERS.LIST.COLUMN_STATUS'),
      filterType: 'string',
      list: getExternalOrderStatusFilterChoices().map(({ value, name, color }) => ({
        id: value,
        name,
        status: value,
        renderValue: () => (
          <InpulseLabel
            color={color}
            style={labelInAdvancedFilterStyle}
            text={name}
            type={'tertiary'}
          />
        ),
      })),
    },
  );

  return filterList;
};
